<script setup lang="ts" generic="T">
  import { register } from 'swiper/element'
  import type { SwiperContainer } from 'swiper/element'
  import type { SwiperOptions } from 'swiper/types'
  import { Swiper } from 'swiper'
  import { Navigation, Pagination, Thumbs, Keyboard, Virtual, A11y, Autoplay } from 'swiper/modules'

  const props = withDefaults(
    defineProps<{
      sliderSettings: SwiperOptions
      slides: T[]
      slideClass?: string
      id?: string
    }>(),
    {
      slideClass: '',
      id: undefined
    }
  )

  const localSliderSettings = computed<SwiperOptions>(() => ({
    ...props.sliderSettings,
    a11y: {
      ...props.sliderSettings.a11y,
      enabled: true,
      containerRoleDescriptionMessage: 'carousel'
    }
  }))

  Swiper.use([Navigation, Pagination, Thumbs, Keyboard, Virtual, A11y, Autoplay])
  register()

  const swiperContainerRef = ref<SwiperContainer | null>(null)

  const emit = defineEmits<{
    (
      e:
        | 'swiperslidechangetransitionstart'
        | 'swiperslidechangetransitionend'
        | 'swiperslideslengthchange'
        | 'swiperactiveindexchange'
        | 'swiperslidechangeend'
        | 'swipernavigationprev'
        | 'swipernavigationnext',
      data: CustomEvent<Swiper[]>
    ): void
    (e: 'swipercannavigatechange', canNavigate: boolean): void
  }>()

  function onSlidesUpdated(event: CustomEvent<Swiper[]>) {
    const swiper = event.detail[0]
    swiper?.slideTo(0)
    emitCanNavigate(swiper)
  }

  function emitCanNavigate(swiper?: Swiper) {
    if (!swiper) return

    const pagesCount = swiper.snapGrid?.length ?? 0
    emit('swipercannavigatechange', pagesCount > 1)
  }

  function disableTabIndex(el: HTMLElement) {
    if (!('prevTabindex' in el.dataset)) {
      el.dataset.prevTabindex = el.getAttribute('tabindex') ?? ''
    }

    el.setAttribute('tabindex', '-1')
  }

  function restoreTabIndex(el: HTMLElement) {
    if (!('prevTabindex' in el.dataset)) return

    const prev = el.dataset.prevTabindex
    delete el.dataset.prevTabindex

    if (prev === undefined || prev === '') {
      el.removeAttribute('tabindex')
      return
    }

    el.setAttribute('tabindex', prev)
  }

  function setSlidesA11y(swiper: Swiper) {
    const visibleClass = swiper.params.slideVisibleClass || 'swiper-slide-visible'

    const slides = Array.from(swiper.slides) as HTMLElement[]

    slides.forEach((slideEl) => {
      const isVisible = slideEl.classList.contains(visibleClass)

      slideEl.setAttribute('aria-hidden', String(!isVisible))

      const interactiveElements = slideEl.querySelectorAll<HTMLElement>(
        'a, button, input, select, textarea, [tabindex]'
      )

      interactiveElements.forEach(isVisible ? restoreTabIndex : disableTabIndex)
    })
  }

  function handleActiveIndexChange(event: CustomEvent<Swiper[]>) {
    const swiper = event.detail[0]
    setSlidesA11y(swiper)
    emitCanNavigate(swiper)
    emit('swiperactiveindexchange', event)
  }

  function handleSlideChangeEnd(event: CustomEvent<Swiper[]>) {
    const swiper = event.detail[0]
    setSlidesA11y(swiper)
    emitCanNavigate(swiper)
    emit('swiperslidechangeend', event)
  }

  function handleSlidesLengthChange(event: CustomEvent<Swiper[]>) {
    onSlidesUpdated(event)

    const swiper = event.detail[0]
    setSlidesA11y(swiper)
    emit('swiperslideslengthchange', event)
  }

  function slideTo(index: number) {
    swiperContainerRef.value?.swiper?.slideTo(index)
  }

  function handleInit(event: CustomEvent<Swiper[]>) {
    const swiper = event.detail[0]
    setSlidesA11y(swiper)
    emitCanNavigate(swiper)
  }

  defineExpose({
    slideTo,
    swiper: computed(() => swiperContainerRef.value?.swiper ?? null),
    swiperEl: swiperContainerRef
  })
</script>

<template>
  <LazyClientOnly>
    <swiper-container
      :id="id"
      ref="swiperContainerRef"
      v-bind="localSliderSettings"
      @swiperinit="handleInit"
      @swiperactiveindexchange="handleActiveIndexChange"
      @swiperslidechangetransitionend="handleSlideChangeEnd"
      @swiperslideslengthchange="handleSlidesLengthChange"
      @swiperslidechangetransitionstart="
        (event: CustomEvent<Swiper[]>) => emit('swiperslidechangetransitionstart', event)
      "
      @swipernavigationprev="(event: CustomEvent<Swiper[]>) => emit('swipernavigationprev', event)"
      @swipernavigationnext="(event: CustomEvent<Swiper[]>) => emit('swipernavigationnext', event)"
    >
      <div slot="container-start">
        <slot name="slider-container-start" />
      </div>

      <swiper-slide
        v-for="(slide, index) in props.slides"
        :key="index"
        :virtual-index="index"
        :class="slideClass"
      >
        <slot
          name="slide"
          v-bind="{ slide, index }"
        />
      </swiper-slide>

      <div slot="container-end">
        <slot name="slider-container-end" />
      </div>
    </swiper-container>

    <slot name="slider-outside" />

    <template #fallback>
      <slot name="fallback">
        <div class="flex h-84 w-full items-center justify-center rounded-md bg-gray-300">
          <UiPlaneLoader />
        </div>
      </slot>
    </template>
  </LazyClientOnly>
</template>
